import {
  createContext,
  type FC,
  type PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';

import type { RegionRepository } from '@/Domain/repositories/RegionRepository';
import { getAll } from '@/modules/region/application/getAll';
import type { Region } from '@/modules/region/domain/Region';
import { createHttpRegionesListRepository } from '@/modules/region/infrastructure/HttpRegionRepository';

interface Props extends PropsWithChildren {
  repository: RegionRepository;
}

interface ProviderValues {
  repository: Props['repository'];
  regions: {
    data: Region[];
    isLoading: boolean;
    error: string | null;
  };
}

const RegionContext = createContext<ProviderValues>({} as ProviderValues);

const RegionProvider: FC<Props> = ({ repository, children }) => {
  const regionesListRepository = createHttpRegionesListRepository();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<Region[]>([]);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const getAllRegions = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const regions = await getAll(regionesListRepository);
        setData(regions);
      } catch (e) {
        setError('Internal Server Error.');
      } finally {
        setIsLoading(false);
      }
    };
    getAllRegions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <RegionContext.Provider
      value={{ repository, regions: { data, isLoading, error } }}
    >
      {children}
    </RegionContext.Provider>
  );
};

export const useRegionContext = () => useContext(RegionContext);

export default RegionProvider;
